import styled from "@emotion/styled";
import { useId } from "react";
import { useForm, FormProvider, FieldValues } from "react-hook-form";
import {
    Button,
    Modal,
    ModalTitle,
    ModalContent,
    ModalActions,
    Typography,
    baseInputClasses,
} from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { TextField } from "@kaltura/mediaspace-shared-ui";

interface NewLinksModalProps {
    open: boolean;
    onSubmit: (data: FieldValues) => void;
    onClose: () => void;
}

const NumericalFieldWrapper = styled.div<{ width?: number }>(({ width = 200 }) => ({
    [`& .${baseInputClasses.root}`]: {
        width,
    },
}));

/**
 * Guest Access - new links modal
 */
export const NewLinksModal = (props: NewLinksModalProps) => {
    const { open, onSubmit, onClose } = props;

    const formId = useId();
    const methods = useForm<FieldValues>({
        defaultValues: {
            name: "",
            clicksLimit: "",
        },
    });

    const { handleSubmit, formState } = methods;
    const { isValid, isDirty, isSubmitting } = formState;

    return (
        <Modal
            open={open}
            fullWidth={true}
            maxWidth="sm"
            // Prevent menu item keydown propagation
            onKeyDown={(e) => e.stopPropagation()}
            // support normal close - escape key, external click
            onClose={onClose}
        >
            <ModalTitle onClose={onClose}>
                <Typography variant={"h3"} component={"div"}>
                    {translate("Create new link")}
                </Typography>
            </ModalTitle>

            <FormProvider {...methods}>
                <ModalContent>
                    <Typography variant="body2">
                        {translate("Allow anyone with this link to access the media without signing in.")}
                    </Typography>

                    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            id="name"
                            label={translate("Link Name")}
                            fontWeightBold={true}
                            displayErrors={true}
                            required={true}
                        />

                        <NumericalFieldWrapper>
                            <TextField
                                id="clicksLimit"
                                fontWeightBold={true}
                                label={translate("# of clicks")}
                                additionalInfo={translate("Limit how many times this link can be accessed.")}
                                placeholder={translate("Unlimited")}
                                type="number"
                                inputProps={{ min: "1" }}
                            />
                        </NumericalFieldWrapper>

                        <NumericalFieldWrapper width={60}>
                            <TextField
                                id="duration"
                                label={translate("Duration")}
                                fontWeightBold={true}
                                postText={translate("Day/s")}
                                type="number"
                                inputProps={{ min: "1" }}
                            />
                        </NumericalFieldWrapper>
                    </form>
                </ModalContent>

                <ModalActions>
                    <Button color="secondary" variant="borderless" onClick={onClose}>
                        {translate("Cancel")}
                    </Button>
                    <Button
                        type="submit"
                        form={formId}
                        color="primary"
                        disabled={!isValid || !isDirty}
                        loading={isSubmitting}
                    >
                        {translate("Create")}
                    </Button>
                </ModalActions>
            </FormProvider>
        </Modal>
    );
};
